import {
    useLayoutEffect,
    useCallback,
    useEffect
} from "react";
import {
    useWindowDimensions,
    ScrollView,
    StyleSheet,
    View
} from "react-native";
import {
    findBlock
} from "./index";
import {
    BLOCK_MESSAGE_SOURCE,
    readBlockViewParams,
    BLOCK_SHARPNESSES,
    BLOCK_DENSITIES
} from "./settings";
import {
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";

type BlockViewSettingsInput = Partial<Record<"gapPropagation" | "paletteKey" | "sharpness" | "themeKey", string | null>>;

const stylesheet = StyleSheet.create({
    empty: {
        justifyContent: "center",
        alignItems: "center",
        flexGrow: 1
    },
    content: {
        flexGrow: 1
    }
});

const BlockView = ({
    id
}: {
    id: string;
}) => {
    const {
        paletteKeys,
        themeKeys,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        height
    } = useWindowDimensions();

    const block = findBlock(id);

    const applySettings = useCallback((input: BlockViewSettingsInput) => {
        NCoreUIKitTheme.switch({
            gapPropagation: BLOCK_DENSITIES.find((density) => density === input.gapPropagation),
            sharpness: BLOCK_SHARPNESSES.find((sharpness) => sharpness === input.sharpness),
            paletteKey: paletteKeys.find((paletteKey) => paletteKey === input.paletteKey),
            themeKey: themeKeys.find((themeKey) => themeKey === input.themeKey)
        });
    }, [
        paletteKeys,
        themeKeys
    ]);

    useLayoutEffect(() => {
        applySettings(readBlockViewParams(window.location.search));
    }, []);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if(event.origin !== window.location.origin || event.data?.source !== BLOCK_MESSAGE_SOURCE) {
                return;
            }

            applySettings(event.data.settings ?? {});
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [
        applySettings
    ]);

    useEffect(() => {
        document.documentElement.style.backgroundColor = colors.content.container.default;

        document.body.style.backgroundColor = colors.content.container.default;
    }, [
        colors
    ]);

    if(!block) {
        return <View
            style={[
                stylesheet.empty,
                {
                    backgroundColor: colors.content.container.default,
                    height
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t("blocks.notFound")}
            </Text>
        </View>;
    }

    const BlockComponent = block.Component;

    return <ScrollView
        style={{
            backgroundColor: colors.content.container.default,
            height
        }}
        contentContainerStyle={stylesheet.content}
    >
        <BlockComponent/>
    </ScrollView>;
};
export default BlockView;
